import { Suspense, lazy, useState } from "react";
import { CLASS_OPTIONS, mergeWithNext, movePages, pagesText, splitAt, updateGroup } from "../pageGroups";

// pdf.js only when the pages are shown (and mocked in tests)
const Thumbs = lazy(() => import("./pdfThumbs").then((m) => ({ default: ThumbsHost(m) })));

// Binds the lazily loaded module to a small host component.
function ThumbsHost(m) {
  return function ThumbsLoaded({ url, onPages, onError, render }) {
    return (
      <m.ThumbDocument url={url} onPages={onPages} onError={onError}>
        {render(m.ThumbPage)}
      </m.ThumbDocument>
    );
  };
}

/*
 * Send to Athena's page groups (owner 10-13; logic in ../pageGroups.js).
 * Each group is one Athena document: a title, its class, its pages as
 * thumbnails. Pages move by drag and drop, or without a mouse (iPad, keyboard):
 * tap pages to select them, then "Move to…". A group splits at a page
 * ("Split here") and merges with the next one. "Not filed" holds pages left
 * out (e.g. the fax cover sheet, marked "Cover").
 *
 * state: { groups: [{key, title, cls, pages}], excluded: [pages] }
 * results: { [group key]: {state: "filing" | "filed" | "error", message} }
 */
export default function PageGroups({
  url,
  numPages,
  onPages,
  state,
  setState,
  cover,
  unreadFrom = null,
  results = {},
  disabled = false,
}) {
  const [picked, setPicked] = useState(() => new Set());
  const [dragOver, setDragOver] = useState(null);
  const [thumbError, setThumbError] = useState(false);
  const locked = (key) => disabled || results[key]?.state === "filed" || results[key]?.state === "filing";

  const toggle = (p) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  const move = (pages, to) => {
    if (!pages.length || to === "") return;
    setState((s) => movePages(s, pages, to));
    setPicked(new Set());
  };
  const lockedPages = new Set(
    state.groups.filter((g) => locked(g.key)).flatMap((g) => g.pages)
  );
  const pickedList = [...picked].filter((p) => !lockedPages.has(p));

  const onDragStart = (e, p) => {
    const pages = picked.has(p) ? pickedList : [p];
    try {
      e.dataTransfer.setData("text/plain", JSON.stringify(pages));
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // a browser without DataTransfer: taps + "Move to…" still work
    }
  };
  const dropProps = (to) => ({
    onDragOver: (e) => {
      if (disabled) return;
      e.preventDefault();
      setDragOver(to);
    },
    onDragLeave: () => setDragOver((d) => (d === to ? null : d)),
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(null);
      if (disabled) return;
      let pages = [];
      try {
        pages = JSON.parse(e.dataTransfer.getData("text/plain"));
      } catch (err) {
        pages = [];
      }
      move((Array.isArray(pages) ? pages : []).filter((p) => !lockedPages.has(p)), to);
    },
  });

  const thumb = (Page, p, groupKey, first) => {
    const on = picked.has(p);
    const isLocked = lockedPages.has(p);
    return (
      <div key={p} className="flex flex-col items-center gap-0.5" data-testid={`page-${p}`}>
        <button
          type="button"
          draggable={!isLocked && !disabled}
          onDragStart={(e) => onDragStart(e, p)}
          onClick={() => !isLocked && toggle(p)}
          aria-pressed={on}
          aria-label={`Page ${p}${cover.has(p) ? " (cover sheet)" : ""}${on ? ", selected" : ""}`}
          disabled={isLocked}
          className={`relative rounded border bg-white p-0.5 ${
            on ? "border-[#1D2951] ring-2 ring-[#1D2951]/30" : "border-[#D0D5DD] hover:border-[#98A2B3]"
          } ${isLocked ? "opacity-60" : ""}`}
        >
          {Page && !thumbError ? (
            <Page page={p} width={64} />
          ) : (
            <div className="flex h-[82px] w-16 items-center justify-center bg-[#F2F4F7] text-xs text-[#667085]">{p}</div>
          )}
          {cover.has(p) && (
            <span className="absolute left-0.5 top-0.5 rounded bg-white/90 px-1 text-[9px] font-medium text-[#475467]">
              Cover
            </span>
          )}
          {unreadFrom && p >= unreadFrom && (
            <span
              className="absolute bottom-0.5 left-0.5 rounded bg-white/90 px-1 text-[9px] text-[#98A2B3]"
              title="The AI read only the first pages; check this one"
            >
              not read
            </span>
          )}
        </button>
        <div className="flex items-center gap-1 text-[10px] text-[#667085]">
          <span>{p}</span>
          {!first && groupKey && !isLocked && !disabled && (
            <button
              type="button"
              className="text-[#2B7FFF] hover:underline"
              onClick={() => setState((s) => splitAt(s, groupKey, p))}
              title={`Start a new document at page ${p}`}
              data-testid={`split-${p}`}
            >
              Split here
            </button>
          )}
        </div>
      </div>
    );
  };

  const body = (Page) => (
    <div className="space-y-2">
      {state.groups.map((g, i) => {
        const r = results[g.key];
        const isLocked = locked(g.key);
        return (
          <section
            key={g.key}
            {...dropProps(g.key)}
            className={`rounded-lg border px-2.5 py-2 ${
              dragOver === g.key ? "border-[#1D2951] bg-[#1D2951]/[0.04]" : "border-[#E4E7EC]"
            }`}
            aria-label={`Document ${i + 1}`}
            data-testid={`group-${i + 1}`}
          >
            <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold text-[#1D2951]">Document {i + 1}</span>
              <input
                type="text"
                value={g.title}
                maxLength={80}
                disabled={isLocked}
                onChange={(e) => setState((s) => updateGroup(s, g.key, { title: e.target.value }))}
                placeholder="Title (optional)"
                aria-label={`Document ${i + 1} title`}
                className="h-7 min-w-[10rem] flex-1 rounded-md border border-[#D0D5DD] px-2 text-xs"
              />
              <select
                value={g.cls}
                disabled={isLocked}
                onChange={(e) => setState((s) => updateGroup(s, g.key, { cls: e.target.value }))}
                aria-label={`Document ${i + 1} class`}
                className="h-7 rounded-md border border-[#D0D5DD] bg-white px-1.5 text-xs"
              >
                {CLASS_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              <span className="text-[#667085]">{pagesText(g.pages)}</span>
              {i + 1 < state.groups.length && !isLocked && !locked(state.groups[i + 1].key) && (
                <button
                  type="button"
                  className="text-[#2B7FFF] hover:underline"
                  onClick={() => setState((s) => mergeWithNext(s, g.key))}
                  data-testid={`merge-${i + 1}`}
                >
                  Merge with next
                </button>
              )}
              {r && (
                <span
                  className={`ml-auto ${
                    r.state === "filed" ? "text-[#027A48]" : r.state === "error" ? "text-[#B42318]" : "text-[#667085]"
                  }`}
                  role={r.state === "error" ? "alert" : undefined}
                  data-testid={`group-${i + 1}-result`}
                >
                  {r.state === "filed" ? "Filed" : r.state === "filing" ? "Filing…" : r.message || "Not filed"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">{g.pages.map((p, j) => thumb(Page, p, g.key, j === 0))}</div>
          </section>
        );
      })}
      <section
        {...dropProps("new")}
        className={`rounded-lg border border-dashed px-2.5 py-1.5 text-xs ${
          dragOver === "new" ? "border-[#1D2951] text-[#1D2951]" : "border-[#D0D5DD] text-[#98A2B3]"
        }`}
        aria-label="New document"
      >
        Drop pages here for a new document
      </section>
      <section
        {...dropProps("excluded")}
        className={`rounded-lg border border-dashed px-2.5 py-1.5 ${
          dragOver === "excluded" ? "border-[#B42318]" : "border-[#D0D5DD]"
        }`}
        aria-label="Not filed"
        data-testid="group-excluded"
      >
        <div className="mb-1 text-xs text-[#667085]">
          <span className="font-semibold">Not filed</span>
          {state.excluded.length
            ? ` · ${pagesText(state.excluded)} stay${state.excluded.length === 1 ? "s" : ""} in the fax only`
            : " · drop pages here to leave them out (e.g. the cover sheet)"}
        </div>
        {state.excluded.length > 0 && (
          <div className="flex flex-wrap gap-2">{state.excluded.map((p) => thumb(Page, p, null, true))}</div>
        )}
      </section>
    </div>
  );

  const targets = [
    ...state.groups
      .filter((g) => !locked(g.key))
      .map((g) => ({ value: g.key, label: `Document ${state.groups.indexOf(g) + 1}${g.title ? `: ${g.title}` : ""}` })),
    { value: "new", label: "New document" },
    { value: "excluded", label: "Not filed" },
  ];

  return (
    <div data-testid="page-groups">
      <div className="mb-1.5 flex min-h-[28px] flex-wrap items-center gap-2 text-xs text-[#475467]">
        {pickedList.length > 0 ? (
          <>
            <span className="font-semibold text-[#1D2951]">
              {pickedList.length} page{pickedList.length === 1 ? "" : "s"} selected
            </span>
            <select
              value=""
              onChange={(e) => move(pickedList, e.target.value)}
              aria-label="Move to"
              className="h-7 rounded-md border border-[#D0D5DD] bg-white px-1.5 text-xs"
              data-testid="move-to"
            >
              <option value="">Move to…</option>
              {targets.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <button type="button" className="underline" onClick={() => setPicked(new Set())}>
              Clear
            </button>
          </>
        ) : (
          <span className="text-[#98A2B3]">Tap pages to select them, then “Move to…” — or drag them.</span>
        )}
      </div>
      <div className="max-h-[42vh] overflow-y-auto pr-1">
        {url && !thumbError ? (
          <Suspense fallback={body(null)}>
            <Thumbs url={url} onPages={onPages} onError={() => setThumbError(true)} render={(Page) => body(Page)} />
          </Suspense>
        ) : (
          body(null)
        )}
      </div>
      {!numPages && !url && (
        <div className="mt-1 text-xs text-[#98A2B3]">The page count is not known yet.</div>
      )}
    </div>
  );
}
